import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { impersonationAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface ImpersonationSession {
  id: number;
  original_user_id: number;
  target_user_id: number;
  original_user_name: string;
  original_user_surname: string;
  target_user_name: string;
  target_user_surname: string;
  started_at: string;
}

const ImpersonationBanner: React.FC = () => {
  const { user } = useAuth();
  const [session, setSession] = useState<ImpersonationSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState<any>(null);
  

  // Definisci loadImpersonationContext prima del useEffect usando useCallback
  const loadImpersonationContext = React.useCallback(async () => {
    try {
      // Carica il contesto di impersonazione
      // Verifica prima se c'è un ID di impersonazione in localStorage
      const impersonatedUserId = localStorage.getItem('impersonated_user_id');
      if (!impersonatedUserId) {
        setSession(null);
        setImpersonatedUser(null);
        return;
      }
      
      try {
        const response = await impersonationAPI.getImpersonationContext();
        
        if (response.active && response.session) {
          setSession(response.session);
          if (response.impersonated_user) {
            setImpersonatedUser(response.impersonated_user);
            // Salva le informazioni della sessione per uso futuro
            localStorage.setItem('impersonation_session_backup', JSON.stringify({
              target_user_name: response.session.target_user_name,
              target_user_surname: response.session.target_user_surname,
              original_user_name: response.session.original_user_name,
              original_user_surname: response.session.original_user_surname,
            }));
          }
        } else {
          // Se l'API restituisce active: false ma c'è ancora impersonated_user_id in localStorage,
          // significa che l'impersonazione è ancora attiva ma l'API non l'ha trovata
          // Usa i dati backup invece di nascondere il banner
          const stillHasId = localStorage.getItem('impersonated_user_id') || localStorage.getItem('impersonated_user_id_backup');
          
          if (stillHasId) {
            // Usa i dati backup per mostrare il banner
            const sessionBackup = localStorage.getItem('impersonation_session_backup');
            
            if (sessionBackup) {
              try {
                const backupData = JSON.parse(sessionBackup);
                const backupSession = {
                  id: 0,
                  original_user_id: 0,
                  target_user_id: parseInt(stillHasId) || 0,
                  original_user_name: backupData.original_user_name || '',
                  original_user_surname: backupData.original_user_surname || '',
                  target_user_name: backupData.target_user_name || '',
                  target_user_surname: backupData.target_user_surname || '',
                  started_at: '',
                };
                setSession(backupSession);
                return;
              } catch (parseErr) {
                // Errore parsing sessione backup
              }
            }
            // Se non abbiamo backup ma c'è ancora impersonated_user_id e user, usa i dati dell'utente corrente
            if (user) {
              const userSession = {
                id: 0,
                original_user_id: 0,
                target_user_id: parseInt(stillHasId) || 0,
                original_user_name: 'Super Admin',
                original_user_surname: '',
                target_user_name: user.nome || '',
                target_user_surname: user.cognome || '',
                started_at: '',
              };
              setSession(userSession);
              setImpersonatedUser(user);
              return;
            }
            // Mostra comunque un banner minimale
            setSession({
              id: 0,
              original_user_id: 0,
              target_user_id: parseInt(stillHasId) || 0,
              original_user_name: 'Super Admin',
              original_user_surname: '',
              target_user_name: 'Utente',
              target_user_surname: '',
              started_at: '',
            });
            return;
          }
          // Solo se non c'è più impersonated_user_id, pulisci tutto
          localStorage.removeItem('impersonated_user_id');
          localStorage.removeItem('impersonated_user_id_backup');
          localStorage.removeItem('impersonation_session_backup');
          localStorage.removeItem('view_admin_mode');
          setSession(null);
          setImpersonatedUser(null);
        }
      } catch (err: any) {
        // Se l'errore è 403 o 500, usa i dati salvati in localStorage come fallback
        if (err.response?.status === 403 || err.response?.status === 500) {
          // Se abbiamo un backup della sessione, usalo per mostrare almeno le informazioni base
          const sessionBackup = localStorage.getItem('impersonation_session_backup');
          if (sessionBackup) {
            try {
              const backupData = JSON.parse(sessionBackup);
              setSession({
                id: 0,
                original_user_id: 0,
                target_user_id: parseInt(impersonatedUserId) || 0,
                original_user_name: backupData.original_user_name || '',
                original_user_surname: backupData.original_user_surname || '',
                target_user_name: backupData.target_user_name || '',
                target_user_surname: backupData.target_user_surname || '',
                started_at: '',
              });
              return;
            } catch (parseErr) {
              // Errore parsing sessione backup
            }
          }
          // Se non abbiamo backup ma c'è ancora impersonated_user_id, mostra comunque il banner
          // usando i dati dell'utente corrente (che è quello impersonato)
          if (impersonatedUserId && user) {
            // Costruisci una sessione minimale usando i dati dell'utente corrente
            setSession({
              id: 0,
              original_user_id: 0,
              target_user_id: parseInt(impersonatedUserId) || 0,
              original_user_name: 'Super Admin',
              original_user_surname: '',
              target_user_name: user.nome || '',
              target_user_surname: user.cognome || '',
              started_at: '',
            });
            setImpersonatedUser(user);
            return;
          }
          // Se non abbiamo né backup né dati utente, nascondi il banner
          setSession(null);
          setImpersonatedUser(null);
          return;
        }
        // Per altri errori, resetta
        setSession(null);
        setImpersonatedUser(null);
      }
    } catch (error) {
      setSession(null);
      setImpersonatedUser(null);
    }
  }, [user]);

  // useEffect che viene eseguito al mount e quando cambia localStorage
  useEffect(() => {
    let isMounted = true;
    
    const checkImpersonation = async () => {
      // Controlla se c'è impersonazione attiva basandosi su localStorage
      const impersonatedUserId = localStorage.getItem('impersonated_user_id') || localStorage.getItem('impersonated_user_id_backup');
      
      if (impersonatedUserId && isMounted) {
        // C'è impersonazione attiva, carica il contesto solo se non è già caricato
        if (!session) {
          await loadImpersonationContext();
        }
      } else if (isMounted) {
        // Non c'è impersonazione attiva, nascondi il banner
        setSession(null);
        setImpersonatedUser(null);
      }
    };
    
    // Esegui subito al mount
    checkImpersonation();
    
    // Ascolta i cambiamenti di localStorage
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'impersonated_user_id' || e.key === 'impersonated_user_id_backup') {
        checkImpersonation();
      }
    };
    
    // Ascolta anche i cambiamenti nello stesso tab (usando un custom event)
    const handleCustomStorageChange = () => {
      checkImpersonation();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('customStorageChange', handleCustomStorageChange);
    
    return () => {
      isMounted = false;
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('customStorageChange', handleCustomStorageChange);
    };
  }, [user?.id]); // Solo quando cambia l'ID utente

  const handleEndImpersonation = async () => {
    setLoading(true);
    try {
      const currentId = localStorage.getItem('impersonated_user_id');
      const backupId = localStorage.getItem('impersonated_user_id_backup');
      
      // Se c'è solo il backup, ripristinalo per poter terminare la sessione
      if (!currentId && backupId) {
        localStorage.setItem('impersonated_user_id', backupId);
      }
      
      // Chiama l'API per terminare la sessione sul backend
      try {
        await impersonationAPI.endImpersonation();
      } catch (apiError) {
        // Continua comunque a pulire localStorage anche se l'API fallisce
      }
      
      // Rimuovi TUTTO da localStorage per assicurarsi che l'impersonazione sia completamente terminata
      localStorage.removeItem('impersonated_user_id');
      localStorage.removeItem('impersonated_user_id_backup');
      localStorage.removeItem('impersonation_session_backup');
      localStorage.removeItem('view_admin_mode');
      
      // Verifica che tutto sia stato rimosso
      const verifyRemoval = localStorage.getItem('impersonated_user_id') || 
                            localStorage.getItem('impersonated_user_id_backup') || 
                            localStorage.getItem('view_admin_mode');
      if (verifyRemoval) {
        // Forza la rimozione
        localStorage.removeItem('impersonated_user_id');
        localStorage.removeItem('impersonated_user_id_backup');
        localStorage.removeItem('impersonation_session_backup');
        localStorage.removeItem('view_admin_mode');
      }
      
      setSession(null);
      setImpersonatedUser(null);
      
      // Ricarica la pagina per aggiornare il contesto e tornare all'area admin
      window.location.href = '/dashboard'; // Reindirizza direttamente alla dashboard admin
    } catch (error) {
      alert('Errore durante la terminazione dell\'impersonazione');
      // Anche in caso di errore, prova a pulire localStorage e ricaricare
      localStorage.removeItem('impersonated_user_id');
      localStorage.removeItem('impersonated_user_id_backup');
      localStorage.removeItem('impersonation_session_backup');
      localStorage.removeItem('view_admin_mode');
      window.location.href = '/dashboard';
    } finally {
      setLoading(false);
    }
  };

  const impersonatedUserIdDebug = localStorage.getItem('impersonated_user_id') || localStorage.getItem('impersonated_user_id_backup');
  
  if (!session) {
    // Se c'è impersonated_user_id ma non c'è sessione, costruisci una sessione minimale
    // Questo può succedere quando l'API non restituisce la sessione ma l'impersonazione è ancora attiva
    if (impersonatedUserIdDebug && user) {
      // Non possiamo chiamare setState durante il render, quindi usiamo un useEffect
      // Ma per ora, restituiamo null e lasciamo che il useEffect gestisca
      // Il problema è che il useEffect potrebbe non essere chiamato di nuovo
      // Quindi dobbiamo forzare un re-render quando la sessione viene impostata
      return null;
    }
    return null;
  }

  return (
    <Alert
      severity="warning"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1300,
        borderRadius: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
      action={
        <Button
          color="inherit"
          size="small"
          onClick={handleEndImpersonation}
          disabled={loading}
          startIcon={<CloseIcon />}
        >
          Termina sessione utente
        </Button>
      }
    >
      <Box>
        <Typography variant="body1" fontWeight="bold">
          ⚠️ Impersonazione Attiva
        </Typography>
        <Typography variant="body2">
          Stai visualizzando come: <strong>{session.target_user_name} {session.target_user_surname}</strong>
          {impersonatedUser && (
            <> ({impersonatedUser.role === 'admin' ? 'Admin' : 'Dipendente'})</>
          )}
          <br />
          <small>Impersonato da: {session.original_user_name} {session.original_user_surname}</small>
        </Typography>
      </Box>
    </Alert>
  );
};

export default ImpersonationBanner;

